<script setup lang="ts">
import Loader from '@/components/Loader/Loader.vue';
import BasePageMaintenanceInfo from '@/components/pages/BasePage/BasePageMaintenanceInfo.vue';
import { useLayoutStore } from '@/stores/layoutStore.ts';
import { truthy } from '@/utils/helper.ts';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import BasePageTitle from './BasePageTitle.vue';
import { onMounted, useTemplateRef } from 'vue';

interface BasePageProps {
  loading?: boolean;
}

const route = useRoute();

withDefaults(defineProps<BasePageProps>(), { loading: false });

const slots = defineSlots<{
  default?(props: unknown): any;
  actions?(props: unknown): any;
  title?(props: unknown): any;
  info?(props: unknown): any;
  status?(props: unknown): any;
}>();

const titleDefaults = {
  title: String(route.name),
};
const layoutStore = useLayoutStore();
const { setPageStatusVisibility, setPageTitleVisibility, setPageElement } = layoutStore;
const { contentTop, titleHeaderHeight, statusBarHeight } = storeToRefs(layoutStore);

function getCssClassList(
  title?: (typeof slots)['title'],
  actions?: (typeof slots)['actions'],
  status?: (typeof slots)['status'],
) {
  setPageStatusVisibility(!!status);
  setPageTitleVisibility(!!title || !!actions);

  return [
    'page',
    layoutStore.pageTitleVisible ? 'page--with-title' : undefined,
    layoutStore.pageStatusVisible ? 'page--with-status' : undefined,
  ]
    .filter(truthy)
    .join(' ');
}

const pageRef = useTemplateRef<HTMLElement>('page');

onMounted(() => {
  if (pageRef.value) {
    setPageElement(pageRef.value);
  }
});
</script>

<template>
  <div ref="page" :class="getCssClassList(slots.title, slots.actions, slots.status)">
    <BasePageMaintenanceInfo />
    <v-toolbar
      v-if="!loading && (slots.title || slots.actions)"
      class="header"
      :height="titleHeaderHeight"
      :extensionHeight="statusBarHeight"
    >
      <v-toolbar-title v-if="slots.title" class="title">
        <slot name="title">
          <BasePageTitle v-bind="titleDefaults" />
        </slot>
      </v-toolbar-title>

      <template v-if="slots.info">
        <div class="info">
          <slot name="info" />
        </div>
      </template>

      <template v-if="slots.actions" #append>
        <div class="actions d-flex align-center">
          <slot name="actions" />
        </div>
      </template>

      <template v-if="slots.status" #extension>
        <div class="status">
          <slot name="status" />
        </div>
      </template>
    </v-toolbar>
    <div v-if="!loading && (slots.title || slots.actions || slots.status)" class="separator">
      &nbsp;
    </div>
    <div class="pageContent">
      <Loader v-if="loading" type="content" />
      <div v-else>
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/mixins';
@use '@/styles/vuetifySettings';

@mixin dynamicWidth() {
  flex-basis: 0;
  flex-grow: 1;
}

.page {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 0;

  min-height: calc(100vh - #{variables.$headerBarHeight} - #{variables.$subNavigationBarHeight});

  .header {
    position: relative;
    z-index: 10;

    width: 100%;
    height: variables.$titleHeaderHeight;

    background: rgb(var(--v-theme-off-white));

    justify-content: space-between;

    @include mixins.shadow-bottom(5);

    .v-toolbar__content > .v-toolbar-title {
      margin-inline-start: 0;
    }
    .title {
      padding-left: 24px;

      @include dynamicWidth();
    }
    .info {
      height: auto;
    }
    :deep(.v-toolbar__append:has(.actions)) {
      @include dynamicWidth();
      justify-content: end;
      margin-inline: 0;

      .actions {
        padding-right: 24px;
        :deep(> *) {
          margin-left: 22px;
        }
      }
    }
    .status {
      width: 100%;
      height: 100%;
      @include mixins.shadow-bottom(5);
    }
  }

  .separator {
    display: block;

    z-index: 10;
    width: 100%;
    height: 0;

    @include mixins.shadow-bottom(5);
  }

  .pageContent {
    display: flex;
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 0;

    overflow-x: auto;
    overflow-y: auto;

    isolation: isolate;
    width: 100%;

    padding-top: vuetifySettings.$spacer * 4;
    padding-right: variables.$horizontalAppPadding;
    padding-bottom: vuetifySettings.$spacer * 22;
    padding-left: variables.$horizontalAppPadding;

    &:has(.pageTabs) {
      padding-top: 0;

      overflow-x: hidden;
      overflow-y: hidden;

      :deep(.base-page-tab-content) {
        height: 100%;
        overflow-x: hidden;
        overflow-y: auto;
      }

      &:has(.page-feedback-wrapper) {
        :deep(.page-feedback-wrapper) {
          min-height: calc(100% - v-bind(contentTop) - #{variables.$tabs-height});
        }
      }
    }
    & > div {
      min-width: 100%;
    }
  }

  .header:has(.status) + .separator + .pageContent {
    &:has(.pageTabs) {
      padding-top: 24px;
    }
  }
}
</style>
